import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import SectionHeader from "../SectionHeader";

const translations = {
    fa: {
        videoAlt: "آموزش راه‌اندازی دلبند",
        videoCaption: "مشاهده ویدیوی آموزش راه‌اندازی",
        regularText: "امروز مراقبت هوشمند را با",
        highlightedText: "دلبند آغاز کنید",
        descriptionHighlight: "با دلبند همیشه از وضعیت سلامتی عزیزانتان مطلع باشید.",
        descriptionText: "هشدارهای هوشمند، پایش لحظه‌ای و ارتباط آسان، همه در یک اپلیکیشن!",
        downloadButton: "دانلود اپلیکیشن",
        closeModal: "بستن",
        tutorialTitle: "ویدیوی آموزش راه‌اندازی و استفاده از دلبند",
    },
    en: {
        videoAlt: "Delband Setup Tutorial",
        videoCaption: "Watch Setup Tutorial Video",
        regularText: "Start Smart Care Today with",
        highlightedText: "Delband",
        descriptionHighlight: "Stay informed about your loved ones' health with Delband.",
        descriptionText: "Smart alerts, real-time monitoring, and easy communication, all in one app!",
        downloadButton: "Download App",
        closeModal: "Close",
        tutorialTitle: "Delband App Setup & Usage Guide Video",
    },
    ar: {
        videoAlt: "دليل إعداد دلبند",
        videoCaption: "مشاهدة فيديو دليل الإعداد",
        regularText: "ابدأ الرعاية الذكية اليوم مع",
        highlightedText: "دلبند",
        descriptionHighlight: "كن على اطلاع دائم بصحة أحبائك مع دلبند.",
        descriptionText: "تنبيهات ذكية، مراقبة في الوقت الفعلي، وتواصل سهل، كل ذلك في تطبيق واحد!",
        downloadButton: "تحميل التطبيق",
        closeModal: "إغلاق",
        tutorialTitle: "فيديو دليل إعداد واستخدام دلبند",
    },
    tr: {
        videoAlt: "Delband Kurulum Eğitimi",
        videoCaption: "Kurulum Eğitim Videosunu İzleyin",
        regularText: "Bugün Akıllı Bakımı Başlatın",
        highlightedText: "Delband ile",
        descriptionHighlight: "Delband ile sevdiklerinizin sağlığı hakkında her zaman bilgi sahibi olun.",
        descriptionText: "Akıllı uyarılar, gerçek zamanlı izleme ve kolay iletişim, hepsi tek bir uygulamada!",
        downloadButton: "Uygulamayı İndir",
        closeModal: "Kapat",
        tutorialTitle: "Delband Kurulum ve Kullanım Eğitim Videosu",
    },
};

const FinalCallToAction: React.FC = () => {
    const { i18n } = useTranslation();
    const currentLang = (i18n.language as keyof typeof translations) || "fa";
    const t = translations[currentLang] || translations.fa;
    const isRTL = ["fa", "ar"].includes(currentLang);

    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    // Escape key listener & body scroll lock when modal opens
    useEffect(() => {
        if (isVideoModalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isVideoModalOpen]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setIsVideoModalOpen(false);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    const scrollToDownload = () => {
        const appStoreSections = document.querySelectorAll("section");
        if (appStoreSections.length >= 2) {
            appStoreSections[1].scrollIntoView({ behavior: "smooth" });
        } else {
            const el = document.getElementById("download") || document.getElementById("app-experience");
            if (el) el.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <motion.section className={`py-12 ${isRTL ? "rtl" : "ltr"}`}>
            <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
                <motion.div
                    className="relative rounded-[32px] bg-gradient-to-br from-white/95 via-blue-50/40 to-white/90 border border-blue-100 shadow-xl overflow-hidden backdrop-blur-md p-6 sm:p-10 md:p-12"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    {/* Background Soft Blue Glows */}
                    <div className="absolute top-0 start-0 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
                    <div className="absolute bottom-0 end-0 w-80 h-80 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative z-10">
                        {/* Video Thumbnail Column */}
                        <div className="md:col-span-6 flex justify-center">
                            <motion.div
                                onClick={() => setIsVideoModalOpen(true)}
                                className="relative w-full aspect-video rounded-2xl overflow-hidden border border-blue-200/80 shadow-lg cursor-pointer group bg-gradient-to-br from-slate-900 via-[#0038A8] to-[#0072CE]"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                transition={{ duration: 0.2 }}
                            >
                                {/* Thumbnail Background Pattern */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent z-10" />
                                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,114,206,0.3)_0,transparent_70%)]" />

                                {/* Thumbnail Info Banner */}
                                <div className="absolute top-4 start-4 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-semibold">
                                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                    <span>HD Tutorial</span>
                                </div>

                                {/* Play Button Aura */}
                                <div className="absolute inset-0 z-20 flex items-center justify-center">
                                    <div className="relative flex items-center justify-center">
                                        <div className="absolute w-20 h-20 rounded-full bg-white/20 group-hover:scale-125 transition-transform duration-500 ease-out" />
                                        <div className="w-16 h-16 rounded-full bg-white text-[#0072CE] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                                            <Play className={`w-7 h-7 fill-current ${isRTL ? "translate-x-[-1px]" : "translate-x-[2px]"}`} />
                                        </div>
                                    </div>
                                </div>

                                {/* Video Caption */}
                                <div className="absolute bottom-4 inset-x-4 z-20 text-center">
                                    <p className="text-white font-bold text-sm sm:text-base drop-shadow-md group-hover:text-blue-200 transition-colors">
                                        {t.videoCaption}
                                    </p>
                                </div>
                            </motion.div>
                        </div>

                        {/* Text & CTA Column */}
                        <div className={`md:col-span-6 flex flex-col justify-center ${isRTL ? "text-right" : "text-left"}`}>
                            <SectionHeader
                                className={isRTL ? "text-right" : "text-left"}
                                regularText={t.regularText}
                                highlightedText={t.highlightedText}
                                descriptionHighlight={t.descriptionHighlight}
                                descriptionText={t.descriptionText}
                            />

                            {/* Main CTA Button */}
                            <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
                                <motion.button
                                    onClick={scrollToDownload}
                                    className="w-full sm:w-auto px-8 py-3.5 rounded-2xl bg-gradient-to-r from-[#0038A8] to-[#0072CE] hover:from-[#002d87] hover:to-[#005fb0] text-white font-bold text-sm sm:text-base shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 flex items-center justify-center gap-2.5 active:scale-95"
                                    whileHover={{ scale: 1.02, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <Download className="w-5 h-5" />
                                    <span>{t.downloadButton}</span>
                                </motion.button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Redesigned Video Modal Overlay */}
            <AnimatePresence>
                {isVideoModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md"
                            onClick={() => setIsVideoModalOpen(false)}
                        />

                        {/* Modal Box */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94, y: 12 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.94, y: 12 }}
                            transition={{ type: "spring", stiffness: 350, damping: 28 }}
                            className="relative z-10 w-full max-w-3xl bg-white rounded-3xl shadow-2xl border border-blue-100 overflow-hidden flex flex-col text-start"
                        >
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-blue-100 text-[#0072CE] flex items-center justify-center">
                                        <Play className="w-5 h-5 fill-current" />
                                    </div>
                                    <h3 className="font-bold text-slate-800 text-sm sm:text-base">
                                        {t.tutorialTitle}
                                    </h3>
                                </div>
                                <button
                                    onClick={() => setIsVideoModalOpen(false)}
                                    className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors"
                                    aria-label="Close video"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Video Player Frame */}
                            <div className="p-4 sm:p-6 bg-slate-950">
                                <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black flex items-center justify-center border border-slate-800 shadow-inner">
                                    {/* Video Player Display */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-[#0038A8]/30 via-slate-900 to-black flex flex-col items-center justify-center p-6 text-center">
                                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0038A8] to-[#0072CE] text-white flex items-center justify-center shadow-2xl mb-4 animate-bounce">
                                            <Play className={`w-8 h-8 fill-current ${isRTL ? "translate-x-[-1px]" : "translate-x-[2px]"}`} />
                                        </div>
                                        <p className="text-white font-semibold text-sm sm:text-base mb-1">
                                            {t.tutorialTitle}
                                        </p>
                                        <p className="text-slate-400 text-xs sm:text-sm">
                                            {t.descriptionText}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="p-4 sm:p-5 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
                                <button
                                    onClick={() => setIsVideoModalOpen(false)}
                                    className="px-6 py-2.5 rounded-xl bg-slate-200/80 hover:bg-slate-300 text-slate-700 font-semibold text-sm transition-colors"
                                >
                                    {t.closeModal}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </motion.section>
    );
};

export default FinalCallToAction;
